import type { TeacherAllocation } from '@/types/allocation';

/** One section a teacher teaches, with every subject they take in it. */
export interface TaughtSection {
  uuid: string;
  /** "9th - B". */
  displayName: string;
  subjects: string[];
  isClassTeacher: boolean;
  totalStudents: number;
}

/**
 * The teacher's allotments, folded into what My Profile counts.
 *
 * An allotment is one subject in one section, so a teacher taking Maths and
 * Science in 9-B has two. Folded by section here, so 9-B and its children are
 * counted once — "Classes Handled" and "Students" would otherwise double.
 */
export function summariseTeaching(allocations: TeacherAllocation[]) {
  const bySection = new Map<string, TaughtSection>();

  for (const allocation of allocations) {
    const section = bySection.get(allocation.classSectionUuid) ?? {
      uuid: allocation.classSectionUuid,
      displayName: allocation.displayName,
      subjects: [],
      isClassTeacher: false,
      totalStudents: allocation.totalStudents,
    };
    if (allocation.subject && !section.subjects.includes(allocation.subject.name)) {
      section.subjects.push(allocation.subject.name);
    }
    section.isClassTeacher ||= allocation.isClassTeacher;
    bySection.set(allocation.classSectionUuid, section);
  }

  const sections = [...bySection.values()].sort((a, b) =>
    a.displayName.localeCompare(b.displayName),
  );
  const subjects = [...new Set(sections.flatMap((section) => section.subjects))].sort((a, b) =>
    a.localeCompare(b),
  );

  return {
    sections,
    subjects,
    students: sections.reduce((sum, section) => sum + section.totalStudents, 0),
  };
}

/** "3 years", "8 months", "Joined this month" — or null when no joining date is recorded. */
export function tenureSince(joinedOn: string | null, today = new Date()): string | null {
  if (!joinedOn) return null;
  const [year, month] = joinedOn.split('-').map(Number);
  const months = (today.getFullYear() - year) * 12 + (today.getMonth() + 1 - month);

  if (months < 1) return 'Joined this month';
  if (months < 12) return `${months} ${months === 1 ? 'month' : 'months'}`;
  const years = Math.floor(months / 12);
  return `${years} ${years === 1 ? 'year' : 'years'}`;
}
